import React, { useRef, useState, cloneElement, useEffect } from 'react';
import type { FC, ReactElement } from 'react';
import cn from 'classnames';
import { getMonthlyPayment } from '@alfa-bank/site-pil-helpers/calculator/pil/get-monthly-payment';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { getAmountWithInsurance as getPilInsurance } from '@alfa-bank/site-pil-helpers/calculator/pil/amount-with-insurance';
import { getAmountWithInsurance as getPledgeInsurance } from '@alfa-bank/site-pil-helpers/calculator/pledge/amount-with-insurance';
import type {
    IPayload,
    TPilSamuraiCalculator,
} from '@alfa-bank/site-pil-samurai-calculator/src/models';

import {
    getOfferTitle,
    groupOffersByCategory,
    useIsSticking,
    getNotificationTitle,
} from './helpers';
import type {
    TPilPreparedCreditOffers,
    TProductId,
    TTargetId,
    TPrepared,
    IOnOfferApply,
    IOnOfferCalculate,
} from './models';
import {
    APPLY_PREPARED_OFFER_EVENT,
    DEFAULT_CATEGORY,
    INITIAL_TARGET,
    pilPreparedKey,
} from './config';

export const PilPreparedCreditOffers: FC<TPilPreparedCreditOffers> = ({
    AnalyticsEventSenderOnMount,
    PersonalizationProvider,
    PilOfferCard,
    Button,
    Tab,
    TabsTags,
    Text,
    cardOffers,
    category,
    children,
    className,
    defaultCategory = DEFAULT_CATEGORY,
    styles,
    personalization,
    pilInsurance,
    pilInterestRate,
    getCardStyler,
    pledgeInsurance,
    pledgeInterestRate,
    useNamedContainersWithChildren,
    NamedContainer,
    CalculatorWithModal,
    cardsLimit,
    analytics,
    showMore,
    url,
    publish,
    sendEvent,
    offerNotificationTitle,
    notificationTitleNewLine,
}) => {
    const { calculatorBlock, disclaimerBlock, titleBlock } =
        useNamedContainersWithChildren(children);
    const [currentAmount, setCurrentAmount] = useState<number>(0);
    const [currentTerm, setCurrentTerm] = useState<number>(0);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentOfferTitle, setCurrentOfferTitle] = useState<string>('');
    const [currentTargetId, setCurrentTargetId] = useState<TTargetId | null>(null);
    const [currentProductId, setCurrentProductId] = useState<TProductId | null>(null);
    /* istanbul ignore next */
    const [preparedOffers, setPrepared, removePrepared] = useLocalStorage<TPrepared | null>(
        pilPreparedKey,
        null
    );
    const [isShowMoreButton, setIsShowMoreButton] = useState(showMore);
    const tabsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        tabsRef.current = document.querySelector('[role="tablist"]') as HTMLDivElement;
    }, []);

    useEffect(() => {
        const { creditAmount, creditTerm, offerTitle, productId, targetId } =
            preparedOffers || INITIAL_TARGET;

        publish(APPLY_PREPARED_OFFER_EVENT, {
            creditParams: {
                amount: creditAmount,
                term: 12 * creditTerm,
                purposeCode: targetId,
            },
            productId,
            showNotification: creditAmount > 0 && creditTerm > 0 && targetId && productId,
            text: getNotificationTitle(
                offerNotificationTitle,
                offerTitle,
                notificationTitleNewLine
            ),
            removePrepared,
        });
    }, [preparedOffers, publish, offerNotificationTitle, notificationTitleNewLine, removePrepared]);
    const { isSticking } = useIsSticking({ elementRef: tabsRef });

    const onOfferApply =
        ({ offerAmount, offerTerm, offerTitle, productId, targetId }: IOnOfferApply) =>
        () => {
            setPrepared({
                offerTitle,
                creditAmount: offerAmount,
                creditTerm: offerTerm,
                targetId,
                productId,
                updatedAt: Date.now(),
            });
            publish(APPLY_PREPARED_OFFER_EVENT, {
                creditParams: {
                    amount: offerAmount,
                    term: 12 * offerTerm,
                    purposeCode: targetId,
                },
                text: getNotificationTitle(
                    offerNotificationTitle,
                    offerTitle,
                    notificationTitleNewLine
                ),
                productId,
                showNotification: true,
                removePrepared,
            });

            if (analytics?.applyPreparedOffer) {
                sendEvent({
                    ...analytics.applyPreparedOffer,
                    product_33: offerTitle,
                });
            }
        };

    const onOfferCalculate =
        ({ offerAmount, offerTerm, offerTitle, productId, targetId }: IOnOfferCalculate) =>
        () => {
            setCurrentAmount(offerAmount);
            setCurrentTerm(offerTerm);
            setCurrentOfferTitle(offerTitle);
            setCurrentTargetId(targetId);
            setCurrentProductId(productId);
            setShowModal(true);

            if (analytics?.clickCalculator) {
                sendEvent(analytics.clickCalculator);
            }
        };

    const onModalClose = () => setShowModal(false);

    const onCalculatorClick = (data?: IPayload) => {
        /* istanbul ignore next */
        const { creditAmount = currentAmount, creditTerm = currentTerm } = data || {};

        setPrepared({
            offerTitle: currentOfferTitle,
            targetId: currentTargetId,
            productId: currentProductId,
            updatedAt: Date.now(),
            creditAmount: creditAmount,
            creditTerm,
        });

        publish(APPLY_PREPARED_OFFER_EVENT, {
            creditParams: {
                amount: creditAmount,
                term: 12 * creditTerm,
                purposeCode: currentTargetId,
            },
            productId: currentProductId,
            text: getNotificationTitle(
                offerNotificationTitle,
                currentOfferTitle,
                notificationTitleNewLine
            ),
            showNotification: true,
            removePrepared,
        });

        onModalClose();
    };
    const showMoreCards = () => setIsShowMoreButton(false);
    const categoriesOffers = groupOffersByCategory(category, cardOffers);

    return (
        <div className={className}>
            {titleBlock}
            <TabsTags
                align="center"
                selectedID={defaultCategory}
                size="xs"
                styler={{ paddingLeft: 0 }}
                tabNamesClassName={cn(styles.external, styles.tagsContainer, {
                    [styles.isSticking]: isSticking,
                })}
            >
                {categoriesOffers.map(({ cardOffers: offers, category: { id, title } }) => {
                    const limit = isShowMoreButton ? Number(cardsLimit) : offers.length;
                    const slicedOffers = offers.slice(0, limit);
                    const isGreaterThanLimit = offers.length > limit;

                    return (
                        <Tab
                            key={id}
                            dataLayer={
                                analytics?.clickCategoryTab && {
                                    ...analytics.clickCategoryTab,
                                    label_detail: `Таб-${title.toLowerCase()}`,
                                }
                            }
                            id={id}
                            keepMounted={false}
                            title={title}
                        >
                            <div className={cn(styles.cardsContainer)}>
                                <PersonalizationProvider {...personalization}>
                                    {slicedOffers.map(
                                        (
                                            {
                                                backgroundColor,
                                                creditAmount,
                                                creditTerm,
                                                hint,
                                                id: offerId,
                                                imgSrc,
                                                isPledge,
                                                personalizationId,
                                                productId,
                                                targetId,
                                                title: offerTitle,
                                                calc = { button: true },
                                            },
                                            index,
                                            filteredOffers
                                        ) => {
                                            const termMonths = 12 * creditTerm;
                                            const amountWithInsurance = isPledge
                                                ? getPledgeInsurance(
                                                      creditAmount,
                                                      termMonths,
                                                      pledgeInsurance
                                                  )
                                                : getPilInsurance(
                                                      creditAmount,
                                                      termMonths,
                                                      pilInsurance
                                                  );
                                            const interestRate = isPledge
                                                ? pledgeInterestRate
                                                : pilInterestRate;
                                            const offerPayment = getMonthlyPayment({
                                                amountWithInsurance,
                                                interestRate,
                                                termMonths,
                                            });

                                            return (
                                                <NamedContainer
                                                    key={offerId}
                                                    name={personalizationId}
                                                >
                                                    <PilOfferCard
                                                        badgeText={hint?.description}
                                                        bgColor={backgroundColor}
                                                        image={imgSrc}
                                                        offerAmount={creditAmount}
                                                        offerPayment={offerPayment}
                                                        offerTerm={creditTerm}
                                                        onOfferApply={onOfferApply({
                                                            offerAmount: creditAmount,
                                                            offerTerm: creditTerm,
                                                            offerTitle,
                                                            targetId,
                                                            productId,
                                                        })}
                                                        onOfferCalculate={onOfferCalculate({
                                                            offerAmount: creditAmount,
                                                            offerTerm: creditTerm,
                                                            offerTitle,
                                                            targetId,
                                                            productId,
                                                        })}
                                                        showCalcButton={calc.button}
                                                        styler={getCardStyler(
                                                            index,
                                                            filteredOffers
                                                        )}
                                                        title={offerTitle}
                                                        url={url}
                                                    />
                                                </NamedContainer>
                                            );
                                        }
                                    )}
                                    {isShowMoreButton && isGreaterThanLimit && (
                                        <Button
                                            block
                                            dataTestId="showMoreButton"
                                            onClick={showMoreCards}
                                            size="s"
                                            styler={{
                                                marginTop: 24,
                                            }}
                                        >
                                            Показать еще
                                        </Button>
                                    )}
                                </PersonalizationProvider>
                            </div>
                        </Tab>
                    );
                })}
            </TabsTags>
            {!!disclaimerBlock && (
                <Text className={cn(styles.disclaimer)} view="secondary-medium">
                    {disclaimerBlock}
                </Text>
            )}
            {!!calculatorBlock && (
                <CalculatorWithModal
                    isOpened={showModal}
                    onClose={onModalClose}
                    title={getOfferTitle(currentOfferTitle)}
                >
                    {cloneElement(calculatorBlock as ReactElement<TPilSamuraiCalculator>, {
                        initialAmount: currentAmount,
                        initialTerm: currentTerm,
                        onClick: onCalculatorClick,
                    })}
                </CalculatorWithModal>
            )}
            {analytics?.viewPreparedOffers && (
                <AnalyticsEventSenderOnMount
                    $event={{
                        data: analytics?.viewPreparedOffers,
                    }}
                />
            )}
        </div>
    );
};
